import React, { useRef, useEffect } from 'react'
import { Box, Typography, TextField, Button, SwipeableDrawer } from '@mui/material'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import EditIcon from '@mui/icons-material/Edit'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { useLocale, useTranslations } from 'next-intl'

import { CountryWithMCC } from '@/src/api/rtkQuery/countryApi'
import ItineraryAutoComplete from '@/src/components/common/itinerary/auto-complete'
import GoogleSignInButton from '@/src/components/common/GoogleSignInButton'
import { UITreatment } from '@/src/services/otpErrorMapping'
import { GoogleUserData } from '@/src/types/auth'

import { OtpStep } from './index'
import HeroBannerCarousel from '@/src/components/common/HeroBannerCarousel'
import GoogleIcon from './GoogleIcon'
import { isAeDomain } from '@/src/utils/generalUtils/isAeDomain'

const OtpInput = MuiOtpInput as any

// Shared TextField styling for disabled Google user fields
const googleFieldSx = {
    mb: 1.75,
    '& .MuiOutlinedInput-root': {
        height: 48,
        fontSize: 15,
        borderRadius: '8px',
        bgcolor: 'grey.100',
        '& fieldset': {
            borderColor: 'grey.300',
            borderWidth: 1.5
        }
    }
}

interface FlowData {
    currentStep: OtpStep
    error: string
    errorState: {
        message: string
        uiTreatment?: UITreatment
        field?: 'email' | 'mobile' | 'countryCode' | 'otp'
    }
    isWelcomeBack: boolean
    existingEmail: string | null
    maskedEmail: string | null
    isGoogleFlow: boolean
    googleUserData: GoogleUserData | null

    // Hero section content (computed in Logic Layer)
    heroTitle: string
    heroSubtitle: string
    heroBrandName: string

    // Mobile data
    mobileCountryCode: string
    selectedCountryCode: CountryWithMCC | null
    countryData: CountryWithMCC[] | undefined
    isCountryDataLoading: boolean
    mobileNumber: string
    mobileOtp: string

    // Email data
    email: string
    emailOtp: string

    // Loading states
    isSendingOtp: boolean
    isVerifyingOtp: boolean

    // Timer states
    mobileResendTimer: number
    emailResendTimer: number
}

interface FlowHandlers {
    onClose: () => void

    // Mobile handlers
    handleCountryCodeChange: (country: CountryWithMCC | null) => void
    handleMobileNumberChange: (value: string) => void
    handleMobileOtpChange: (value: string) => void
    handleSendMobileOtp: () => void
    handleVerifyMobileOtp: () => void
    handleResendMobileOtp: () => void
    handleBackToMobileInput: () => void

    // Email handlers
    handleEmailChange: (value: string) => void
    handleEmailOtpChange: (value: string) => void
    handleSendEmailOtp: () => void
    handleVerifyEmailOtp: () => void
    handleResendEmailOtp: () => void
    handleBackToEmailInput: () => void

    // Google Sign In handlers
    handleGoogleSignIn: (userData: GoogleUserData) => void
    handleSendGoogleMobileOtp: () => void
    handleVerifyGoogleMobileOtp: () => void
    handleResendGoogleMobileOtp: () => void
}

interface OtpVerificationModalMobileProps {
    open: boolean
    flowData: FlowData
    flowHandlers: FlowHandlers
}

const OtpVerificationModalMobile: React.FC<OtpVerificationModalMobileProps> = ({ open, flowData, flowHandlers }) => {
    const {
        currentStep,
        error,
        errorState,
        isWelcomeBack,
        existingEmail,
        maskedEmail,
        isGoogleFlow,
        googleUserData,
        heroTitle,
        heroSubtitle,
        heroBrandName,
        mobileCountryCode,
        selectedCountryCode,
        countryData,
        isCountryDataLoading,
        mobileNumber,
        mobileOtp,
        email,
        emailOtp,
        isSendingOtp,
        isVerifyingOtp,
        mobileResendTimer,
        emailResendTimer
    } = flowData

    const {
        onClose,
        handleCountryCodeChange,
        handleMobileNumberChange,
        handleMobileOtpChange,
        handleSendMobileOtp,
        handleVerifyMobileOtp,
        handleResendMobileOtp,
        handleBackToMobileInput,
        handleEmailChange,
        handleEmailOtpChange,
        handleSendEmailOtp,
        handleVerifyEmailOtp,
        handleResendEmailOtp,
        handleBackToEmailInput,
        handleGoogleSignIn,
        handleSendGoogleMobileOtp,
        handleVerifyGoogleMobileOtp,
        handleResendGoogleMobileOtp
    } = flowHandlers

    const locale = useLocale()
    const t = useTranslations('otpModal')

    // Ref for the form content container
    const formContentRef = useRef<HTMLDivElement>(null)

    // Auto-scroll to form when drawer opens or step changes
    useEffect(() => {
        if (open && formContentRef.current) {
            // Small delay to ensure drawer animation completes
            const timer = setTimeout(() => {
                formContentRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                })
            }, 300)

            return () => clearTimeout(timer)
        }
    }, [open, currentStep])

    // Shared mobile input form component to reduce duplication
    const renderMobileNumberInput = (showGoogleFields: boolean = false) => (
        <>
            {showGoogleFields && (
                <>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.75 }}>
                        <TextField
                            label={t('firstName')}
                            value={googleUserData?.firstName || 'N/A'}
                            disabled
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    height: 48,
                                    fontSize: 15,
                                    borderRadius: '8px',
                                    bgcolor: 'grey.100',
                                    '& fieldset': {
                                        borderColor: 'grey.300',
                                        borderWidth: 1.5
                                    }
                                }
                            }}
                        />

                        <TextField
                            label={t('lastName')}
                            value={googleUserData?.lastName || 'N/A'}
                            disabled
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    height: 48,
                                    fontSize: 15,
                                    borderRadius: '8px',
                                    bgcolor: 'grey.100',
                                    '& fieldset': {
                                        borderColor: 'grey.300',
                                        borderWidth: 1.5
                                    }
                                }
                            }}
                        />
                    </Box>

                    <TextField label={t('email')} value={email} disabled fullWidth sx={googleFieldSx} />
                </>
            )}

            <TextField
                label={showGoogleFields ? t('mobileNumber') : undefined}
                placeholder="985-768-9009"
                type="tel"
                value={mobileNumber}
                onChange={e => handleMobileNumberChange(e.target.value)}
                fullWidth
                inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    dir: locale === 'ar' ? 'rtl' : 'ltr'
                }}
                InputProps={{
                    startAdornment: (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                pr: 0,
                                pl: 0,
                                borderRight: '1px solid',
                                borderColor: 'grey.300',
                                mr: 1,
                                minWidth: 80,
                                maxWidth: 80
                            }}
                        >
                            <Box
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: 'auto',
                                        fontSize: 16,
                                        border: 'none',
                                        padding: 0,
                                        '& fieldset': {
                                            border: 'none'
                                        },
                                        '&:hover fieldset': {
                                            border: 'none'
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none'
                                        }
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        padding: '0 2px 0 4px'
                                    },
                                    '& .MuiAutocomplete-endAdornment': {
                                        right: '2px',
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '16px'
                                        }
                                    }
                                }}
                            >
                                <ItineraryAutoComplete
                                    label=""
                                    disableClearable
                                    loading={isCountryDataLoading}
                                    options={countryData || []}
                                    value={selectedCountryCode as any}
                                    onChange={(value: any) => handleCountryCodeChange(value as CountryWithMCC | null)}
                                    getOptionLabel={(option: any) => {
                                        const country = option as CountryWithMCC
                                        return `+ ${country.mobileCountryCode} (${country[locale as keyof CountryWithMCC]})`
                                    }}
                                    renderValueForMobileScreen={(value: any) => {
                                        const country = value as CountryWithMCC
                                        return `+ ${country?.mobileCountryCode}`
                                    }}
                                    renderOption={(props: any, option: any) => {
                                        const country = option as CountryWithMCC
                                        return (
                                            <Typography {...props} key={props.key} sx={{ fontSize: 14 }}>
                                                {`+ ${country.mobileCountryCode} (${country[locale as keyof CountryWithMCC]})`}
                                            </Typography>
                                        )
                                    }}
                                />
                            </Box>
                        </Box>
                    )
                }}
                sx={{
                    mb: 1.75,
                    '& .MuiOutlinedInput-root': {
                        height: 48,
                        fontSize: 15,
                        borderRadius: '8px',
                        '& fieldset': {
                            borderColor: 'grey.300',
                            borderWidth: 1.5
                        },
                        '&:hover fieldset': {
                            borderColor: 'grey.400'
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 2
                        }
                    }
                }}
            />

            {error && (
                <Typography sx={{ color: 'error.main', fontSize: 14, mb: 2 }} role="alert" aria-live="assertive">
                    {error}
                </Typography>
            )}

            <Button
                fullWidth
                variant="contained"
                onClick={showGoogleFields ? handleSendGoogleMobileOtp : handleSendMobileOtp}
                disabled={isSendingOtp}
                sx={{
                    height: 44,
                    fontSize: 15,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    mb: showGoogleFields ? 0 : 2,
                    boxShadow: 'none',
                    bgcolor: 'primary.main',
                    '&:hover': {
                        boxShadow: 'none',
                        bgcolor: 'primary.dark'
                    },
                    '&.Mui-disabled': {
                        bgcolor: 'grey.300',
                        color: 'white'
                    }
                }}
            >
                {isSendingOtp ? t('sending') : t('continue')}
            </Button>

            {!showGoogleFields && (
                <GoogleSignInButton
                    onSuccess={handleGoogleSignIn}
                    onError={() => {
                        // Error handled by GoogleSignInButton component
                    }}
                    buttonText={t('continueWithGoogle')}
                    fullWidth
                    startIcon={<GoogleIcon />}
                    sx={{
                        height: 44,
                        fontSize: 15,
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '8px',
                        mb: 0,
                        backgroundColor: 'white',
                        color: 'text.primary',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                            backgroundColor: 'grey.50',
                            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)'
                        }
                    }}
                />
            )}
        </>
    )

    const renderHeroSection = () => (
        <HeroBannerCarousel height="55vh" minHeight={350}>
            {/* Login Flow Handle - positioned at the top of hero image */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 15,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    pt: 1,
                    pb: 1
                }}
            >
                <Box sx={{ width: 32, height: 4, bgcolor: '#FFFFFF', borderRadius: 2 }} />
            </Box>

            <Box
                sx={{
                    position: 'absolute',
                    zIndex: 10,
                    textAlign: 'center',
                    px: 3,
                    bottom: 40,
                    left: 0,
                    right: 0
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: 20,
                        lineHeight: 1.2,
                        color: 'white',
                        letterSpacing: '-0.3px'
                    }}
                >
                    {heroTitle}
                </Typography>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: 1.2,
                        color: 'white',
                        letterSpacing: '-0.3px'
                    }}
                >
                    {isAeDomain() ? `${heroSubtitle} ` : ''}
                    {/* <Box component="span" sx={{ color: 'warning.main', fontWeight: 400 }}>
                        {heroBrandName}
                    </Box> */}
                </Typography>
            </Box>
        </HeroBannerCarousel>
    )

    const renderBenefits = () => (
        <Box
            sx={{
                px: 3,
                py: 1.5,
                height: 30,
                bgcolor: 'grey.900',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5
            }}
        >
            <ConfirmationNumberIcon
                sx={{
                    width: 20,
                    height: 20,
                    color: 'primary.main'
                }}
            />
            <Typography
                sx={{
                    fontSize: 14,
                    color: 'white',
                    fontWeight: 500,
                    lineHeight: 1.4
                }}
            >
                {t('viewAllBookings')}
            </Typography>
        </Box>
    )

    const renderMobileInputStep = () => (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {renderHeroSection()}
            {renderBenefits()}

            <Box
                ref={formContentRef}
                sx={{
                    px: 3,
                    pt: 4,
                    pb: 8,
                    flex: 1,
                    bgcolor: 'white',
                    mt: 0
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: 18,
                        color: 'text.primary',
                        lineHeight: 1.2,
                        letterSpacing: '-0.3px',
                        textAlign: 'center'
                    }}
                >
                    {t('enterYourMobileNumber')}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: 'text.secondary',
                        mb: 1.75,
                        lineHeight: 1.5,
                        textAlign: 'center'
                    }}
                >
                    {t('mobileVerificationDescription')}
                </Typography>

                {renderMobileNumberInput(false)}
            </Box>
        </Box>
    )

    const renderMobileVerificationStep = () => (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {renderHeroSection()}

            <Box
                ref={formContentRef}
                sx={{
                    px: 3,
                    pt: 4,
                    pb: 8,
                    flex: 1,
                    bgcolor: 'white',
                    mt: 0
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: 18,
                        color: 'text.primary',
                        lineHeight: 1.2,
                        letterSpacing: '-0.3px',
                        textAlign: 'center'
                    }}
                >
                    {t('verifyMobileNumber')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.75 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: 'text.secondary',
                            lineHeight: 1.5
                        }}
                    >
                        {t('mobileOtpDescription', { mobileCountryCode, mobileNumber })}
                    </Typography>
                    <Typography
                        component="span"
                        onClick={handleBackToMobileInput}
                        sx={{
                            fontSize: 14,
                            color: 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 600
                        }}
                    >
                        <EditIcon sx={{ fontSize: 16 }} />
                        {t('changeNumber')}
                    </Typography>
                </Box>

                <Box sx={{ mb: 1.75, display: 'flex', justifyContent: 'center' }}>
                    <OtpInput
                        value={mobileOtp}
                        onChange={(value: string) => handleMobileOtpChange(value)}
                        length={4}
                        autoFocus
                        TextFieldsProps={{
                            size: 'small'
                        }}
                        sx={{
                            gap: 1,
                            '& .MuiOutlinedInput-root': {
                                width: 45,
                                height: 45,
                                '& fieldset': {
                                    borderColor: 'grey.300',
                                    borderRadius: '8px',
                                    borderWidth: 1.5
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey.400'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2
                                }
                            },
                            '& .MuiOutlinedInput-input': {
                                textAlign: 'center',
                                fontSize: 18,
                                fontWeight: 600,
                                color: 'text.primary'
                            }
                        }}
                    />
                </Box>

                {error && (
                    <Typography
                        sx={{ color: 'error.main', fontSize: 14, mb: 2.5, textAlign: 'center' }}
                        role="alert"
                        aria-live="assertive"
                    >
                        {error}
                    </Typography>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    onClick={isGoogleFlow ? handleVerifyGoogleMobileOtp : handleVerifyMobileOtp}
                    disabled={isVerifyingOtp || mobileOtp.length !== 4}
                    sx={{
                        height: 44,
                        fontSize: 15,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        mb: 1.75,
                        boxShadow: 'none',
                        bgcolor: 'primary.main',
                        '&:hover': {
                            boxShadow: 'none',
                            bgcolor: 'primary.dark'
                        },
                        '&.Mui-disabled': {
                            bgcolor: 'grey.300',
                            color: 'white'
                        }
                    }}
                >
                    {isVerifyingOtp ? t('verifying') : t('continue')}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                    <Button
                        variant="text"
                        onClick={isGoogleFlow ? handleResendGoogleMobileOtp : handleResendMobileOtp}
                        disabled={mobileResendTimer > 0}
                        sx={{
                            height: 44,
                            fontSize: 15,
                            fontWeight: 600,
                            textTransform: 'none',
                            color: 'primary.main',
                            '&.Mui-disabled': {
                                color: 'text.disabled'
                            },
                            '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {mobileResendTimer > 0 ? t('resendCodeTimer', { timer: mobileResendTimer }) : t('resendCode')}
                    </Button>
                </Box>
            </Box>
        </Box>
    )

    const renderEmailInputStep = () => (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {renderHeroSection()}

            <Box
                ref={formContentRef}
                sx={{
                    px: 3,
                    pt: 4,
                    pb: 8,
                    flex: 1,
                    bgcolor: 'white',
                    mt: 0
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: isWelcomeBack ? 24 : 18,
                        color: 'text.primary',
                        lineHeight: 1.2,
                        letterSpacing: '-0.3px',
                        textAlign: 'center'
                    }}
                >
                    {isWelcomeBack ? t('welcomeBack') : t('completeRegistration')}
                </Typography>
                {isWelcomeBack && existingEmail ? (
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: 16,
                            color: 'text.primary',
                            mb: 1.5,
                            lineHeight: 1.5,
                            textAlign: 'center',
                            fontWeight: 400
                        }}
                    >
                        {t('verifyEmailAddressSubtitle')}
                    </Typography>
                ) : null}
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: 'text.secondary',
                        mb: 1.75,
                        lineHeight: 1.5,
                        textAlign: 'center'
                    }}
                >
                    {isWelcomeBack && maskedEmail
                        ? t('welcomeBackMessage', { email: maskedEmail })
                        : !existingEmail
                          ? t('enterEmailToComplete')
                          : t('emailVerificationDescription')}
                </Typography>

                <TextField
                    placeholder={t('emailAddress')}
                    type="email"
                    value={email}
                    onChange={e => handleEmailChange(e.target.value)}
                    fullWidth
                    disabled={isWelcomeBack && !!existingEmail}
                    inputProps={{
                        dir: locale === 'ar' ? 'rtl' : 'ltr'
                    }}
                    sx={{
                        mb: 1.75,
                        '& .MuiOutlinedInput-root': {
                            height: 48,
                            fontSize: 15,
                            borderRadius: '8px',
                            '& fieldset': {
                                borderColor: 'grey.300',
                                borderWidth: 1.5
                            },
                            '&:hover fieldset': {
                                borderColor: 'grey.400'
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: 2
                            }
                        }
                    }}
                />

                {error && (
                    <Typography sx={{ color: 'error.main', fontSize: 14, mb: 2 }} role="alert" aria-live="assertive">
                        {error}
                    </Typography>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSendEmailOtp}
                    disabled={isSendingOtp}
                    sx={{
                        height: 44,
                        fontSize: 15,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        mb: 2,
                        boxShadow: 'none',
                        bgcolor: 'primary.main',
                        '&:hover': {
                            boxShadow: 'none',
                            bgcolor: 'primary.dark'
                        },
                        '&.Mui-disabled': {
                            bgcolor: 'grey.300',
                            color: 'white'
                        }
                    }}
                >
                    {isSendingOtp ? t('sending') : t('continue')}
                </Button>

                <Button
                    fullWidth
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                        height: 44,
                        fontSize: 15,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'primary.50'
                        }
                    }}
                >
                    {t('skipForNow')}
                </Button>
            </Box>
        </Box>
    )

    const renderEmailVerificationStep = () => (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {renderHeroSection()}

            <Box
                ref={formContentRef}
                sx={{
                    px: 3,
                    pt: 4,
                    pb: 8,
                    flex: 1,
                    bgcolor: 'white',
                    mt: 0
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: 18,
                        color: 'text.primary',
                        lineHeight: 1.2,
                        letterSpacing: '-0.3px',
                        textAlign: 'center'
                    }}
                >
                    {t('verifyEmailAddress')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.75 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: 'text.secondary',
                            lineHeight: 1.5
                        }}
                    >
                        {t('emailOtpDescription', { email })}
                    </Typography>
                    <Typography
                        component="span"
                        onClick={handleBackToEmailInput}
                        sx={{
                            fontSize: 14,
                            color: 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 600
                        }}
                    >
                        <EditIcon sx={{ fontSize: 16 }} />
                        {t('edit')}
                    </Typography>
                </Box>

                <Box sx={{ mb: 1.75, display: 'flex', justifyContent: 'center' }}>
                    <OtpInput
                        value={emailOtp}
                        onChange={(value: string) => handleEmailOtpChange(value)}
                        length={4}
                        autoFocus
                        TextFieldsProps={{
                            size: 'small'
                        }}
                        sx={{
                            gap: 1,
                            '& .MuiOutlinedInput-root': {
                                width: 45,
                                height: 45,
                                '& fieldset': {
                                    borderColor: 'grey.300',
                                    borderRadius: '8px',
                                    borderWidth: 1.5
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey.400'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2
                                }
                            },
                            '& .MuiOutlinedInput-input': {
                                textAlign: 'center',
                                fontSize: 18,
                                fontWeight: 600,
                                color: 'text.primary'
                            }
                        }}
                    />
                </Box>

                {error && (
                    <Typography
                        sx={{ color: 'error.main', fontSize: 14, mb: 2.5, textAlign: 'center' }}
                        role="alert"
                        aria-live="assertive"
                    >
                        {error}
                    </Typography>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleVerifyEmailOtp}
                    disabled={isVerifyingOtp || emailOtp.length !== 4}
                    sx={{
                        height: 44,
                        fontSize: 15,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        mb: 1.75,
                        boxShadow: 'none',
                        bgcolor: 'primary.main',
                        '&:hover': {
                            boxShadow: 'none',
                            bgcolor: 'primary.dark'
                        },
                        '&.Mui-disabled': {
                            bgcolor: 'grey.300',
                            color: 'white'
                        }
                    }}
                >
                    {isVerifyingOtp ? t('verifying') : t('continue')}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                    <Button
                        variant="text"
                        onClick={handleResendEmailOtp}
                        disabled={emailResendTimer > 0}
                        sx={{
                            height: 44,
                            fontSize: 15,
                            fontWeight: 600,
                            textTransform: 'none',
                            color: 'primary.main',
                            '&.Mui-disabled': {
                                color: 'text.disabled'
                            },
                            '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {emailResendTimer > 0 ? t('resendCodeTimer', { timer: emailResendTimer }) : t('resendCode')}
                    </Button>
                </Box>
            </Box>
        </Box>
    )

    const renderGoogleMobileInputStep = () => (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {renderHeroSection()}
            {renderBenefits()}

            <Box
                ref={formContentRef}
                sx={{
                    px: 3,
                    pt: 4,
                    pb: 8,
                    flex: 1,
                    bgcolor: 'white',
                    mt: 0
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: 18,
                        color: 'text.primary',
                        lineHeight: 1.2,
                        letterSpacing: '-0.3px',
                        textAlign: 'center'
                    }}
                >
                    {t('completeRegistration')}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: 'text.secondary',
                        mb: 1.75,
                        lineHeight: 1.5,
                        textAlign: 'center'
                    }}
                >
                    {t('provideMobileNumberDescription')}
                </Typography>

                {renderMobileNumberInput(true)}
            </Box>
        </Box>
    )

    const renderCurrentStep = () => {
        switch (currentStep) {
            case OtpStep.MOBILE_INPUT:
                return renderMobileInputStep()
            case OtpStep.MOBILE_VERIFICATION:
                return renderMobileVerificationStep()
            case OtpStep.EMAIL_INPUT:
                return renderEmailInputStep()
            case OtpStep.EMAIL_VERIFICATION:
                return renderEmailVerificationStep()
            case OtpStep.GOOGLE_MOBILE_INPUT:
                return renderGoogleMobileInputStep()
            default:
                return renderMobileInputStep()
        }
    }

    const shouldRenderContent = open

    return (
        <SwipeableDrawer
            open={open}
            onClose={onClose}
            onOpen={() => {}}
            anchor="bottom"
            disableSwipeToOpen={false}
            ModalProps={{ keepMounted: false }}
            slotProps={{
                paper: {
                    sx: {
                        height: '90dvh !important',
                        maxHeight: '90dvh !important',
                        borderTopLeftRadius: '20px !important',
                        borderTopRightRadius: '20px !important',
                        overflow: 'hidden !important',
                        backgroundColor: 'rgba(0, 0, 0, 0.3) !important',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        flexDirection: 'column',
                        border: 'none',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '60px',
                            backgroundColor: 'transparent',
                            borderTopLeftRadius: '20px',
                            borderTopRightRadius: '20px',
                            zIndex: 1
                        }
                    }
                }
            }}
        >
            {shouldRenderContent ? renderCurrentStep() : null}
        </SwipeableDrawer>
    )
}

export { OtpVerificationModalMobile }
export type { OtpVerificationModalMobileProps }
